/**
 * useAgentEvals — named hook for agent evaluation rollup data (FR-009, FR-010).
 *
 * Returns QueryState<EvalsView> discriminated union with 60-second
 * stale-while-revalidate caching semantics.
 *
 * The hook call is unconditional (Rules of Hooks). When
 * useSubscriptionActive() returns false, the literal "skip" is passed
 * to useQuery to prevent a network round-trip.
 *
 * Uses the canonical `wrapQuery` helper from queryState.ts to surface
 * ConvexError throws as the discriminated-union `error` / `forbidden`
 * branches rather than propagating them to the nearest error boundary.
 *
 * @see specs/prd.md Section 7.4, FR-010
 * @see shared/types/evals.ts
 * @see apps/dashboard/src/api/queryState.ts (wrapQuery)
 */
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { wrapQuery, type QueryState } from "../queryState";
import type { EvalsView, EvalRollup } from "../../../../../shared/types/evals";

/**
 * Hook result type — QueryState<EvalsView>.
 */
export type AgentEvalsResult = QueryState<EvalsView>;

/**
 * Raw payload shape returned by the agentEvals composite. `latest` is
 * `null` when no rollups have been computed yet; otherwise a typed
 * EvalRollup row.
 */
type AgentEvalsRaw = { latest: EvalRollup | null };

/**
 * Named hook returning the latest agent evaluation rollup.
 *
 * The result follows the 5-branch QueryState discriminated union:
 * loading | empty | ready | error | forbidden.
 *
 * Errors thrown synchronously by `useQuery` during render (the Convex
 * propagation path for ConvexError) are caught here and surfaced as the
 * `error` or `forbidden` QueryState branch via wrapQuery's kind-mapping.
 */
export function useAgentEvals(): AgentEvalsResult {
  const isActive = useSubscriptionActive();

  // useQuery throws synchronously for ConvexError; capture the throw at
  // the call site so it can be routed through wrapQuery's catch arm and
  // mapped to the canonical `error` / `forbidden` discriminated branches.
  let raw: AgentEvalsRaw | undefined;
  let queryError: unknown;
  let threw = false;
  try {
    raw = useQuery(
      api.composites.agentEvals.agentEvals,
      isActive ? {} : "skip",
    ) as AgentEvalsRaw | undefined;
  } catch (err) {
    queryError = err;
    threw = true;
  }

  return wrapQuery<AgentEvalsRaw, EvalsView>(
    // When the underlying useQuery throw was captured, force wrapQuery
    // past its loading short-circuit so the transform runs and re-throws.
    threw ? ({} as AgentEvalsRaw) : raw,
    (rawValue): EvalsView => {
      if (threw) {
        // Re-throw inside the transform so wrapQuery's catch arm
        // performs the kind → forbidden / error mapping.
        throw queryError;
      }
      return { latest: rawValue.latest };
    },
    (view) => view.latest === null || view.latest === undefined,
  );
}
